<template>
  <v-card class="sw-card" flat outlined>
    <div class="sw-marker" :aria-label="markerLetter">{{ markerLetter }}</div>
    <div class="sw-card-inner">
      <div class="sw-body">
        <div v-if="!readonly" class="sw-actions">
          <div v-if="saving" class="sw-saving-wrap" :aria-label="$t('FieldSurveyForm.saving')">
            <v-progress-circular indeterminate size="22" width="2" color="#0091d5" />
          </div>
          <template v-else-if="editing">
            <v-btn
              icon
              small
              class="sw-icon-btn"
              :disabled="!canSave"
              :aria-label="$t('FieldSurveyForm.save')"
              @click="submitEdit"
            >
              <v-icon size="22" color="#43a047">mdi-check</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              class="sw-icon-btn"
              :aria-label="$t('FieldSurveyForm.cancel')"
              @click="cancelEdit"
            >
              <v-icon size="22" color="#e53935">mdi-close</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn icon small class="sw-icon-btn" :disabled="deleting" :aria-label="$t('FieldSurveyForm.edit')" @click="startEdit">
              <v-icon size="18" color="#888">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon small class="sw-icon-btn" :disabled="deleting || editing" :aria-label="$t('FieldSurveyForm.delete')" @click="deleteDialog = true">
              <v-icon size="18" color="#888">mdi-delete-outline</v-icon>
            </v-btn>
          </template>
        </div>

        <template v-if="!editing">
          <div class="sw-title">{{ viewName || '—' }}</div>
          <div class="sw-row">
            <v-icon class="sw-row-icon" size="16" color="#666">account-hard-hat</v-icon>
            <span class="sw-row-text">{{ viewUser || '—' }}</span>
          </div>
          <div v-if="serviceRows.length" class="sw-services">
            <v-chip v-for="service in workServices" :key="service" small class="ma-1">{{ $t('EquipmentServices.' + service) }}</v-chip>
          </div>
          <div class="sw-row">
            <v-icon class="sw-row-icon" size="16" color="#666">mdi-map-marker-outline</v-icon>
            <span class="sw-row-text">{{ viewCoordinates || '—' }}</span>
          </div>
          <div class="sw-row sw-row-file">
            <v-icon class="sw-row-icon" size="16" :color="viewHasChart ? '#0091d5' : '#999'">
              {{ viewHasChart ? 'mdi-file-document-outline' : 'mdi-file-remove-outline' }}
            </v-icon>
            <button
              v-if="canOpenStoredChart"
              type="button"
              class="sw-file-open"
              :disabled="openingChart"
              :aria-label="$t('FieldSurveyForm.chartView')"
              @click="openStoredChart"
            >
              {{ viewChartLabel }}
            </button>
            <span v-else :class="viewHasChart ? 'sw-file-link' : 'sw-file-muted'">{{ viewChartLabel }}</span>
          </div>
        </template>

        <template v-else>
          <v-text-field
            v-model="draft.deviceName"
            class="sw-mat-input sw-name-input"
            flat
            solo
            dense
            hide-details
            single-line
            :placeholder="$t('FieldSurveyForm.fieldName')"
          />

          <div class="sw-field-row sw-field-row-select">
            <v-icon class="sw-field-row-icon" size="20" color="#444">mdi-account-hard-hat</v-icon>
            <survey-work-technician-select
              v-model="draft.userId"
              class="sw-field-grow"
              :items="technicianItems"
              :loading="technicianOptionsLoading"
              :disabled="saving"
              :compact="true"
              :field-survey-id="fieldSurveyId"
              @user-created="$emit('technician-user-created', $event)"
            />
          </div>

          
          <survey-work-services v-if="serviceRows.length" v-model="draft.services" :rows="editableServiceRows" :disabled="saving" />
          <div class="sw-field-row sw-field-row-coords">
            <v-icon class="sw-field-row-icon" size="20" color="#444">mdi-map-marker-outline</v-icon>
            <v-text-field
              v-model="draft.coordinates"
              class="sw-mat-input sw-field-grow"
              flat
              solo
              dense
              single-line
              :placeholder="$t('FieldSurveyForm.fieldCoordinates')"
              :hint="$t('NewCampaignInitialData.devicesAndServices.coordinatesHint')"
              :rules="coordinateRules"
              hide-details="auto"
              validate-on-blur
            />
          </div>

          <input
            ref="chartFileInput"
            type="file"
            class="sw-file-input"
            accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
            :disabled="saving"
            @change="onChartFileSelected"
          >

          <button
            v-if="showChartUploadPill"
            type="button"
            class="sw-chart-upload-pill"
            :disabled="saving"
            @click="openChartFilePicker"
          >
            <v-icon size="22" color="#757575">mdi-upload</v-icon>
            <span>{{ $t('FieldSurveyForm.chartUploadButtonLabel') }}</span>
          </button>

          <div v-else class="sw-chart-when-has-file mt-3">
            <div class="sw-chart-status">
              <button
                v-if="canOpenEditChart"
                type="button"
                class="sw-file-open"
                :disabled="saving || openingChart"
                :aria-label="$t('FieldSurveyForm.chartView')"
                @click="openEditChart"
              >
                {{ editChartPathLabel }}
              </button>
              <span v-else>{{ $t('FieldSurveyForm.chartWillRemove') }}</span>
            </div>
            <div class="sw-chart-btns">
              <template v-if="pendingChartFile">
                <v-btn text small color="grey darken-1" class="px-1" :disabled="saving" @click="clearPendingChartFile">
                  {{ $t('FieldSurveyForm.chartClearSelection') }}
                </v-btn>
              </template>
              <template v-else-if="markChartRemoved && initialHadChart">
                <v-btn text small color="#0091d5" class="px-1" :disabled="saving" @click="openChartFilePicker">
                  {{ $t('FieldSurveyForm.chartReplace') }}
                </v-btn>
                <v-btn text small color="grey darken-1" class="px-1" :disabled="saving" @click="undoRemoveChart">
                  {{ $t('FieldSurveyForm.chartUndoRemove') }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn text small color="#0091d5" class="px-1" :disabled="saving" @click="openChartFilePicker">
                  {{ $t('FieldSurveyForm.chartReplace') }}
                </v-btn>
                <v-btn text small color="grey darken-1" class="px-1" :disabled="saving" @click="removeChartForSave">
                  {{ $t('FieldSurveyForm.chartRemove') }}
                </v-btn>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>

    <v-dialog
      v-model="deleteDialog"
      max-width="420"
      content-class="sw-delete-dialog-overlay"
      @click:outside="deleteDialog = false"
    >
      <v-card class="sw-delete-dialog-card" flat>
        <v-btn
          icon
          class="sw-delete-close"
          :aria-label="$t('FieldSurveyForm.cancel')"
          :disabled="deleting"
          @click="deleteDialog = false"
        >
          <v-icon color="#1e3a4c">mdi-close</v-icon>
        </v-btn>
        <div class="sw-delete-inner">
          <p class="sw-delete-message">{{ $t('FieldSurveyForm.deleteConfirmMessage') }}</p>
          <div class="sw-delete-actions">
            <v-btn
              class="sw-delete-btn-cancel"
              outlined
              depressed
              :disabled="deleting"
              @click="deleteDialog = false"
            >
              {{ $t('FieldSurveyForm.cancel') }}
            </v-btn>
            <v-btn
              class="sw-delete-btn-confirm"
              depressed
              :loading="deleting"
              @click="confirmDelete"
            >
              {{ $t('FieldSurveyForm.delete') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import SurveyWorkServices from './SurveyWorkServices.vue'
import SurveyWorkTechnicianSelect from './SurveyWorkTechnicianSelect.vue'
import { validateLatLon } from '@/lib/helpers'
import { PONCE_TECHNICIAN_SELECT_VALUE } from '@/lib/surveyWorkTechnicians'
import {
  getSurveyWorkPluviometricChartDownloadURL,
  openLocalFileInNewTab
} from '@/lib/surveyWorkPluviometricChart'
import { openResolvedUrlInNewTab } from '@/lib/openResolvedUrlInNewTab'
import { lockedServiceKeys } from '@/lib/surveyWorkServices'

export default {
  name: 'SurveyWorkCard',
  components: { SurveyWorkTechnicianSelect, SurveyWorkServices },
  props: {
    serviceRows: { type: Array, default: () => [] },
    work: {
      type: Object,
      required: true
    },
    markerLetter: {
      type: String,
      required: true
    },
    saving: {
      type: Boolean,
      default: false
    },
    deleting: {
      type: Boolean,
      default: false
    },
    technicianItems: {
      type: Array,
      default: () => []
    },
    technicianOptionsLoading: {
      type: Boolean,
      default: false
    },
    fieldSurveyId: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      editing: false,
      deleteDialog: false,
      pendingChartFile: null,
      markChartRemoved: false,
      initialHadChart: false,
      /** Shown in view mode while parent save is in flight */
      optimisticUI: null,
      openingChart: false,
      draft: {
        services: [],
        deviceName: '',
        userId: '',
        coordinates: ''
      }
    }
  },
  computed: {
    memberWorks () {
      return Array.isArray(this.work.works) ? this.work.works : []
    },
    lockedKeys () {
      return lockedServiceKeys(this.memberWorks)
    },
    workServices () {
      const keys = Array.isArray(this.work.serviceKeys) ? this.work.serviceKeys.slice() : []
      this.lockedKeys.forEach((key) => {
        if (!keys.includes(key)) keys.push(key)
      })
      return keys
    },
    editableServiceRows () {
      return this.serviceRows.map(row => ({
        ...row,
        blocked: row.assigned - (this.workServices.includes(row.key) ? 1 : 0) >= row.contracted,
        locked: this.lockedKeys.includes(row.key)
      }))
    },
    hasChart () {
      const p = this.work.pluviometricChart
      return !!(p && String(p).trim())
    },
    hasChartEffective () {
      if (this.pendingChartFile) return true
      if (this.markChartRemoved) return false
      return this.hasChart
    },
    showChartUploadPill () {
      return this.editing && !this.hasChartEffective
    },
    chartLabel () {
      return this.hasChart
        ? this.$t('FieldSurveyForm.pluviometricChartYes')
        : this.$t('FieldSurveyForm.pluviometricChartNo')
    },
    viewName () {
      if (this.saving && this.optimisticUI) return this.optimisticUI.deviceName
      return this.work.deviceName || ''
    },
    viewUser () {
      if (this.saving && this.optimisticUI) return this.optimisticUI.user
      if (this.work.ponceTechnician) return this.$t('FieldSurveyForm.ponceTechnicianOption')
      return this.work.userDisplay || ''
    },
    selectedTechnicianLabel () {
      const id = this.draft.userId
      if (!id) return ''
      if (id === PONCE_TECHNICIAN_SELECT_VALUE) return this.$t('FieldSurveyForm.ponceTechnicianOption')
      const it = this.technicianItems.find((x) => x.value === id)
      return it ? it.text : ''
    },
    viewCoordinates () {
      if (this.saving && this.optimisticUI) return this.optimisticUI.coordinates
      return this.work.coordinates || ''
    },
    viewHasChart () {
      if (this.saving && this.optimisticUI) {
        const c = this.optimisticUI.chartChange
        if (c === 'replace') return true
        if (c === 'remove') return false
      }
      return this.hasChart
    },
    viewChartLabel () {
      return this.viewHasChart
        ? this.$t('FieldSurveyForm.pluviometricChartYes')
        : this.$t('FieldSurveyForm.pluviometricChartNo')
    },
    chartBasename () {
      const p = this.work.pluviometricChart
      if (!p || !String(p).trim()) return ''
      const parts = String(p).trim().split('/')
      return parts[parts.length - 1] || ''
    },
    editChartPathLabel () {
      if (this.pendingChartFile) return this.pendingChartFile.name
      return this.chartBasename
    },
    pluviometricChartChange () {
      if (this.pendingChartFile) return 'replace'
      if (this.markChartRemoved && this.initialHadChart) return 'remove'
      return 'none'
    },
    canOpenStoredChart () {
      return this.hasChart && this.viewHasChart
    },
    canOpenEditChart () {
      if (this.pendingChartFile) return true
      return this.hasChart && !this.markChartRemoved
    },
    coordinateRules () {
      return [
        v => !!String(v || '').trim() || this.$t('Config.fieldIsRequired'),
        v => validateLatLon(v) || this.$t('Config.allCoordinatesRule')
      ]
    },
    canSave () {
      if (this.serviceRows.length && !this.draft.services.length) return false
      if (this.lockedKeys.some((key) => !this.draft.services.includes(key))) return false
      if (this.editableServiceRows.some((row) => (
        this.draft.services.includes(row.key) &&
        !this.workServices.includes(row.key) &&
        row.blocked
      ))) return false
      return (
        String(this.draft.deviceName || '').trim() !== '' &&
        String(this.draft.userId || '').trim() !== '' &&
        validateLatLon(String(this.draft.coordinates || ''))
      )
    }
  },
  watch: {
    readonly (now) {
      if (now && this.editing) this.cancelEdit()
    },
    work () {
      if (!this.editing) this.syncDraftFromWork()
    },
    saving (now, prev) {
      if (prev && !now) {
        this.optimisticUI = null
      }
    }
  },
  created () {
    this.syncDraftFromWork()
  },
  methods: {
    syncDraftFromWork () {
      this.draft = {
        services: this.workServices,
        deviceName: this.work.deviceName || '',
        userId: this.work.ponceTechnician ? PONCE_TECHNICIAN_SELECT_VALUE : (this.work.userId || ''),
        coordinates: this.work.coordinates || ''
      }
      this.pendingChartFile = null
      this.markChartRemoved = false
    },
    resetChartDraft () {
      this.pendingChartFile = null
      this.markChartRemoved = false
    },
    startEdit () {
      if (this.readonly) return
      this.syncDraftFromWork()
      this.initialHadChart = this.hasChart
      this.editing = true
    },
    cancelEdit () {
      this.editing = false
      this.syncDraftFromWork()
    },
    openChartFilePicker () {
      const el = this.$refs.chartFileInput
      if (el) el.click()
    },
    onChartFileSelected (e) {
      const input = e.target
      const f = input.files && input.files[0]
      if (input) input.value = ''
      if (!f) return
      this.pendingChartFile = f
      this.markChartRemoved = false
    },
    clearPendingChartFile () {
      this.pendingChartFile = null
    },
    removeChartForSave () {
      this.pendingChartFile = null
      this.markChartRemoved = true
    },
    undoRemoveChart () {
      this.markChartRemoved = false
    },
    showChartOpenError () {
      if (this.$root.snack) {
        this.$root.snack.show({ text: this.$t('UI.serverError'), error: true })
      }
    },
    async openStoredChart () {
      const path = String(this.work.pluviometricChart || '').trim()
      if (!path || this.openingChart) return
      const opened = openResolvedUrlInNewTab(() => getSurveyWorkPluviometricChartDownloadURL(path))
      this.openingChart = true
      try {
        await opened
      } catch (e) {
        this.showChartOpenError()
      } finally {
        this.openingChart = false
      }
    },
    openEditChart () {
      if (this.pendingChartFile) {
        openLocalFileInNewTab(this.pendingChartFile)
        return
      }
      this.openStoredChart()
    },
    submitEdit () {
      if (!this.canSave) return
      const deviceName = String(this.draft.deviceName).trim()
      const ponceTechnician = this.draft.userId === PONCE_TECHNICIAN_SELECT_VALUE
      const userId = ponceTechnician ? '' : String(this.draft.userId).trim()
      const coordinates = String(this.draft.coordinates).trim()
      const chartChange = this.pluviometricChartChange
      this.$emit('save', {
        equipmentId: this.work.equipmentId || this.work.id,
        services: this.draft.services.slice(),
        deviceName,
        userId,
        ponceTechnician,
        coordinates,
        pluviometricChartChange: chartChange,
        pluviometricChartFile: this.pendingChartFile,
        onComplete: (ok) => {
          if (!ok) return
          this.editing = false
          this.resetChartDraft()
        }
      })
    },
    confirmDelete () {
      this.$emit('delete', this.work.equipmentId || this.work.id)
      this.deleteDialog = false
    }
  }
}
</script>

<style scoped lang="stylus">
$sw-primary = #0098E1

.sw-card
  position relative
  overflow visible !important
  border-radius 10px !important
  border-color rgba(0, 32, 47, 0.08) !important
  margin-bottom 12px
  background #fff !important

.sw-marker
  position absolute
  z-index 2
  top 0
  left 0
  min-width 25px
  height 25px
  padding 0 4px
  box-sizing border-box
  border-radius 999px !important
  background $sw-primary
  color #fff
  font-weight 700
  font-size 0.8125rem
  display flex
  align-items center
  justify-content center
  line-height 1
  transform translate(-42%, -42%)
  box-shadow 0 2px 8px rgba(0, 32, 47, 0.2)
  white-space nowrap


.sw-card-inner
  padding 12px 12px 14px 44px

.sw-body
  position relative
  min-width 0
  padding-top 2px

.sw-actions
  position absolute
  top 0
  right 0
  display flex
  gap 0

.sw-icon-btn
  margin 0

.sw-saving-wrap
  display flex
  align-items center
  justify-content center
  width 36px
  height 36px
  margin-right -4px

.sw-title
  font-weight 700
  font-size 18px
  color #333
  padding-right 72px
  line-height 1.35
  margin-bottom 10px

.sw-row
  display flex
  align-items flex-start
  gap 10px
  font-size 16px
  color #666
  margin-bottom 6px
  line-height 1.4

.sw-row-icon
  margin-top 3px
  flex-shrink 0

.sw-row-text
  word-break break-word

.sw-row-file
  margin-bottom 0

.sw-file-link
  color $sw-primary
  font-weight 500

.sw-file-open
  display inline
  margin 0
  padding 0
  border none
  background transparent
  font inherit
  font-weight 500
  color $sw-primary
  cursor pointer
  text-align left
  line-height inherit

.sw-file-open:hover:not(:disabled)
  text-decoration underline

.sw-file-open:disabled
  opacity 0.65
  cursor wait

.sw-file-muted
  color #999

.sw-field-row
  display flex
  align-items flex-end
  gap 10px
  margin-top 14px

.sw-field-row-select,
.sw-field-row-coords
  align-items flex-start

.sw-field-row-select .sw-field-row-icon,
.sw-field-row-coords .sw-field-row-icon
  margin-top 10px
  margin-bottom 0

.sw-field-row-icon
  flex-shrink 0
  margin-bottom 10px

.sw-field-grow
  flex 1 1 auto
  min-width 0

.sw-name-input
  margin-top 4px
  padding-right 56px

.sw-mat-input >>> .v-input__control
  min-height 36px

.sw-mat-input >>> .v-input__slot
  margin-bottom 0
  padding 4px 0 !important
  min-height 36px !important
  background transparent !important
  border-radius 0 !important
  box-shadow none !important
  border-bottom 1px solid rgba(0, 32, 47, 0.38) !important

.sw-mat-input >>> .v-input__slot:before,
.sw-mat-input >>> .v-input__slot:after
  display none !important

.sw-mat-input >>> .v-text-field__details
  padding 4px 0 0
  margin 0

.sw-mat-input >>> input
  font-size 16px
  color #333
  padding-top 2px

.sw-mat-input >>> .v-label
  font-size 14px

.sw-name-input >>> input
  font-weight 700
  font-size 18px

.sw-file-input
  position absolute
  width 0
  height 0
  opacity 0
  pointer-events none

.sw-chart-upload-pill
  display flex
  align-items center
  justify-content center
  gap 10px
  width 100%
  margin-top 16px
  padding 14px 16px
  background #eef1f5
  border 1px solid #d5dde5
  border-radius 10px
  cursor pointer
  color #757575
  font-size 16px
  font-weight 500
  font-family inherit
  transition background 0.15s ease

.sw-chart-upload-pill:hover:not(:disabled)
  background #e4e9ef

.sw-chart-upload-pill:disabled
  opacity 0.6
  cursor default

.sw-chart-when-has-file
  padding-top 2px

.sw-chart-status
  font-size 15px
  color #333
  line-height 1.4
  word-break break-word
  margin-bottom 8px

.sw-chart-status .sw-file-open
  word-break break-all

.sw-chart-btns
  display flex
  flex-wrap wrap
  align-items center
  gap 4px 8px
</style>

<style lang="stylus">
/* Dialog content is attached to body; unscoped so overlay + card styles apply */
.sw-delete-dialog-overlay
  box-shadow 0 8px 32px rgba(0, 32, 47, 0.12) !important
  border-radius 12px !important
  overflow hidden

.sw-delete-dialog-card
  position relative
  padding 44px 28px 32px
  border-radius 12px !important
  background #fff !important

.sw-delete-close
  position absolute !important
  top 6px
  right 6px
  margin 0 !important

.sw-delete-inner
  text-align center

.sw-delete-message
  margin 0 auto 28px
  max-width 300px
  font-size 1.0625rem
  line-height 1.5
  font-weight 400
  color #2c3e50
  letter-spacing -0.01em

.sw-delete-actions
  display flex
  justify-content center
  align-items center
  flex-wrap wrap
  gap 24px

.sw-delete-btn-cancel
  min-width 120px
  height 34px !important
  min-height 34px !important
  padding 0 14px !important
  border-radius 8px !important
  text-transform none !important
  font-weight 600
  font-size 0.875rem
  letter-spacing 0.02em
  border-width 1px !important
  border-color #c5ced6 !important
  color #2c3e50 !important
  background #fff !important

.sw-delete-btn-cancel::before,
.sw-delete-btn-confirm::before
  border-radius 8px !important

.sw-delete-btn-confirm
  min-width 120px
  height 34px !important
  min-height 34px !important
  padding 0 14px !important
  border-radius 8px !important
  text-transform none !important
  font-weight 600
  font-size 0.875rem
  letter-spacing 0.02em
  background #ff3b30 !important
  border-color #ff3b30 !important
  color #fff !important
  box-shadow none !important

.sw-delete-btn-cancel .v-btn__content,
.sw-delete-btn-confirm .v-btn__content
  padding 0 2px !important
  min-height 0 !important
  line-height 1.2
</style>



// WEBPACK FOOTER //
// SurveyWorkCard.vue?62728734